import { cva, type VariantProps } from 'class-variance-authority';
import type { HTMLAttributes, ReactNode } from 'react';

import { cn } from '@/lib/utils';

// A design terv badge-rendszere: pill-forma, 20px magas, 11.5px/500, PUHA tónusú hátterek (soft bg + telített
// szöveg). A SZÍN a `variant` (szemantikus tónus), a felirat ELŐTTI jel opcionális: `dot` (kör-pötty, currentColor)
// VAGY `icon` (tetszőleges vezető elem — pl. Heroicon vagy egyedi marker). A brand = MORGENS-zöld tónus.
const badgeVariants = cva(
  'inline-flex h-5 items-center gap-[5px] whitespace-nowrap rounded-full px-2 text-xs font-medium transition-colors [&>svg]:size-3',
  {
    variants: {
      variant: {
        default: 'bg-muted text-foreground/80',
        secondary: 'bg-muted text-foreground/80',
        muted: 'bg-sunken text-muted-foreground',
        outline: 'border-border text-muted-foreground border bg-transparent',
        success: 'bg-success-soft text-success',
        warning: 'bg-warning-soft text-warning',
        danger: 'bg-danger-soft text-danger-600',
        destructive: 'bg-danger-soft text-danger-600',
        info: 'bg-info-soft text-info',
        sky: 'bg-sky-soft text-sky',
        violet: 'bg-violet-soft text-violet',
        brand: 'bg-brand-soft text-brand-dark dark:text-brand',
      },
    },
    defaultVariants: { variant: 'default' },
  },
);

export interface BadgeProps
  extends HTMLAttributes<HTMLSpanElement>, VariantProps<typeof badgeVariants> {
  /** Kis kör-pötty a felirat előtt (currentColor). Az `icon` felülírja. */
  dot?: boolean;
  /** Tetszőleges vezető ikon/marker a felirat előtt (pl. Heroicon vagy egyedi span). Elsőbbség a `dot`-tal szemben. */
  icon?: ReactNode;
}

function Badge({ className, variant, dot, icon, children, ...props }: BadgeProps): JSX.Element {
  return (
    <span className={cn(badgeVariants({ variant }), className)} {...props}>
      {icon ? (
        <span className="flex shrink-0 items-center [&>svg]:size-3" aria-hidden="true">
          {icon}
        </span>
      ) : dot ? (
        <span className="size-1.5 shrink-0 rounded-full bg-current" aria-hidden="true" />
      ) : null}
      {children}
    </span>
  );
}

export { Badge, badgeVariants };
